import React, { useState, useEffect } from 'react';
import './NewsletterModal.css';
import { getSendyListIds } from '../services/configService';
import { API_URL } from '../config';

const NewsletterModal = ({ 
  isOpen, 
  onClose, 
  title = "Subscribe to Our Newsletter",
  buttonText = "Subscribe",
  listType = "general",
  successMessage = "¡Gracias por suscribirte!"
}) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [listId, setListId] = useState('');

  // Cargar la configuración al iniciar
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await getSendyListIds();
        const idKey = `${listType}ListId`;
        setListId(config[idKey]);
      } catch (error) {
        console.error('Error al cargar configuración:', error);
      }
    };
    
    if (isOpen) {
      loadConfig();
    }
  }, [isOpen, listType]);

  if (!isOpen) return null;

  const validateEmail = (email) => {
    // Longitud máxima según RFC 5321
    if (email.length > 254) return false;
    
    // Expresión regular más robusta que valida:
    // - Caracteres permitidos en la parte local
    // - Estructura del dominio
    // - Longitud de las partes
    const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    if (!regex.test(email)) return false;
    
    // Validaciones adicionales
    const [localPart, domain] = email.split('@');
    
    // La parte local no puede empezar ni terminar con punto
    if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
    
    // La parte local no puede tener puntos consecutivos
    if (localPart.includes('..')) return false;
    
    // El dominio debe tener al menos un punto y un TLD válido
    if (!domain.includes('.') || domain.split('.').some(part => part.length === 0)) return false;
    
    // El TLD debe tener entre 2 y 63 caracteres
    const tld = domain.split('.').pop();
    if (tld.length < 2 || tld.length > 63) return false;
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    setSubmitStatus('');
    setIsLoading(true);

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    if (!listId) {
      setEmailError('System error: Could not determine list ID');
      setIsLoading(false);
      return;
    }

    try {
      console.log(`Iniciando proceso de suscripción a lista ${listType}`);

      const response = await fetch(`${API_URL}/newsletter/subscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify({
          email: email,
          name: null,
          lists: [listType]
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Error de servidor' }));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log('Suscripción exitosa');
        setSubmitStatus(successMessage);
        setEmail('');
        setTimeout(() => {
          onClose();
          setSubmitStatus('');
        }, 2000);
      } else {
        console.error('Error en suscripción:', data.message);
        setEmailError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error de conexión:', error.message);
      setEmailError('Connection error. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Portal directo al body para evitar problemas de posicionamiento
  return (
    <div className="newsletter-global-wrapper">
      {/* Overlay con posición fija */}
      <div 
        className="newsletter-overlay" 
        onClick={onClose}
      ></div>
      
      {/* Modal centrado absolutamente */}
      <div className="newsletter-modal-container">
        <div className="newsletter-modal-content">
          <button className="newsletter-close-btn" onClick={onClose}>×</button>
          <h3 className="newsletter-title">{title}</h3>
          
          <form onSubmit={handleSubmit} className="newsletter-form">
            <div className="newsletter-input-group">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className={`newsletter-input ${emailError ? 'newsletter-input-error' : ''}`}
              />
              <button 
                type="submit" 
                className="newsletter-submit-btn"
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : buttonText}
              </button>
            </div>
            
            {emailError && <p className="newsletter-error-message">{emailError}</p>}
            {submitStatus && <p className="newsletter-success-message">{submitStatus}</p>}
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewsletterModal;